/**
 * Tiny fetch wrapper.
 *
 * - Production: app and API share an origin (t.pa-app.net). The Worker
 *   serves the React bundle for non-`/v1/*` paths and handles the API
 *   on `/v1/*`. Same-origin → `fetch("/v1/...")` just works.
 * - Dev: Vite proxies `/v1/*` to wrangler dev on :8787 from the same
 *   :5173 origin — also same-origin from the browser's view.
 * - `credentials: "same-origin"` is the explicit default that sends
 *   the `dash_session` cookie on same-origin requests and refuses
 *   it cross-origin (defense in depth).
 * - Forwards the `x-d1-bookmark` header on reads so D1 Sessions API
 *   gives us read-your-writes consistency across replicas.
 */
import type {
  DecisionRow,
  DuckdbMetricsResponse,
  IntradayScan,
  NavRow,
  PostmortemRow,
  StatusResponse,
  StrategyKey,
  ThesisRow,
  TraderJournalEntry,
} from "./types";

// VITE_API_BASE is optional — leave unset and `fetch("/v1/...")` resolves
// against the current origin. Set it only for unusual dev setups where
// the app is hosted somewhere else than the API.
const BASE = (import.meta.env.VITE_API_BASE ?? "").replace(/\/$/, "");
const BOOKMARK_HEADER = "x-d1-bookmark";

let lastBookmark: string | null = null;

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
  }
}

async function request<T>(
  path: string,
  init?: RequestInit & { skipBookmark?: boolean },
): Promise<T> {
  const headers = new Headers(init?.headers);
  if (lastBookmark && !init?.skipBookmark) {
    headers.set(BOOKMARK_HEADER, lastBookmark);
  }
  const r = await fetch(`${BASE}${path}`, {
    ...init,
    credentials: "same-origin",
    headers,
  });
  const bookmark = r.headers.get(BOOKMARK_HEADER);
  if (bookmark) lastBookmark = bookmark;
  if (!r.ok) {
    let msg = `HTTP ${r.status}`;
    try {
      const body = await r.json();
      if (body && typeof body === "object" && "error" in body) {
        msg = String((body as { error: unknown }).error);
      }
    } catch {
      /* non-JSON body */
    }
    throw new ApiError(r.status, msg);
  }
  return (await r.json()) as T;
}

export const api = {
  status: () => request<StatusResponse>("/v1/status"),
  nav: (strategy: StrategyKey, since: string) =>
    request<{ strategy: StrategyKey; since: string; rows: NavRow[] }>(
      `/v1/nav?strategy=${strategy}&since=${since}`,
    ),
  decisions: (strategy: StrategyKey, day?: string) =>
    request<{ strategy: StrategyKey; day: string; rows: DecisionRow[] }>(
      `/v1/decisions?strategy=${strategy}` + (day ? `&day=${day}` : ""),
    ),
  theses: (strategy: StrategyKey, state?: string) =>
    request<{ strategy: StrategyKey; state: string; rows: ThesisRow[] }>(
      `/v1/theses?strategy=${strategy}` + (state ? `&state=${state}` : ""),
    ),
  postmortems: (strategy: StrategyKey, since: string) =>
    request<{ strategy: StrategyKey; since: string; rows: PostmortemRow[] }>(
      `/v1/postmortems?strategy=${strategy}&since=${since}`,
    ),
  intradayScans: (limit: number = 24) =>
    request<{ rows: IntradayScan[] }>(`/v1/intraday-scans?limit=${limit}`),
  traderJournal: (limit: number = 10) =>
    request<{ rows: TraderJournalEntry[] }>(`/v1/trader-journal?limit=${limit}`),
  duckdbMetrics: () => request<DuckdbMetricsResponse>("/v1/duckdb-metrics"),
  login: (password: string) =>
    request<{ ok: true }>("/v1/login", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ password }),
      skipBookmark: true,
    }),
  logout: () =>
    request<{ ok: true }>("/v1/logout", { method: "POST", skipBookmark: true }),
  health: () =>
    request<{ ok: true; env: string }>("/v1/health", { skipBookmark: true }),
};
